/**
 * Application bootstrap — wires the WireClient to the world-model store
 * and the chat store, then publishes the client handle for UI intent
 * dispatch.
 *
 * Transport selection (P1.8 C1 update):
 *   - Default: real `WebSocketWireClient` pointing at
 *     `VITE_GATEWAY_URL` (falls back to `ws://localhost:7000/ws`).
 *   - Opt-in mock: set `VITE_USE_MOCK=1` to run against
 *     `WireMockServer(freshHandshake)` for offline UI iteration.
 *     (The pre-P1.8 default was the mock; P1.6 WorldHost is live now.)
 *
 * Server-message routing:
 *   - World-model-affecting messages flow through
 *     `useWorldModel.applyServerMessage`.
 *   - Chat-affecting envelopes are demuxed here into `useChat`:
 *       `Spoke`               → appendZoneMessage
 *       `ZoneSystemMessage`   → appendSystemMessage
 *       `WorldChat` envelope  → appendWorldBroadcast
 *       `ZoneBufferSnapshot`  → replaceZoneBuffer
 *   - DM envelopes into `useDms`; `Error` into `useToasts`;
 *     `PresenceUpdate` into `usePresence`.
 */

import { useAuth } from './auth';
import { freshHandshake } from './__fixtures__/wire/fresh-handshake';
import { useChat } from './chat/store';
import { useDms } from './dms/store';
import { phase0, debugLog } from './log';
import { createSelfPresenceDriver, usePresence } from './presence';
import { useThrottle } from './throttle';
import { useToasts } from './toasts';
import {
  useLiveWire,
  WebSocketWireClient,
  WireMockServer,
  type ClientHandshake,
  type ServerMessage,
  type WireClient,
} from './wire';
import { useWorldModel } from './world';

const DEFAULT_GATEWAY_URL = 'ws://localhost:7000/ws';
const HANDSHAKE: ClientHandshake = {
  protocol_version: 1,
  client_build: 'mytile-client/0.0.0+dev',
  capabilities: [],
  // GH#7 C1: Phase 1 has no localStorage persistence of the world store
  // (zustand is wiped on browser reload), so we always advertise empty
  // state. The server then promotes any in-linger session from
  // `Replay → Full`, delivering a complete snapshot the empty client
  // can hydrate from. Phase 1.5+ adds persistence + a freshness check
  // and flips this to `true` when the local store can apply a replay.
  has_local_state: false,
};

function resolveGatewayUrl(): string {
  const url = import.meta.env['VITE_GATEWAY_URL'] as string | undefined;
  return url && url.length > 0 ? url : DEFAULT_GATEWAY_URL;
}

/** True when the caller wants the offline mock transport. Default off
 *  (P1.8 C1); set `VITE_USE_MOCK=1` to opt in for UI-only iteration. */
function shouldUseMock(): boolean {
  const flag = import.meta.env['VITE_USE_MOCK'] as string | undefined;
  return flag === '1' || flag === 'true';
}

/** Seconds until the token expires.  Returns 0 when the token is already
 *  expired or has no parseable `exp` claim. */
function tokenSecondsRemaining(token: string): number {
  try {
    const parts = token.split('.');
    if (parts.length !== 3) return 0;
    const payload = JSON.parse(
      atob(parts[1]!.replace(/-/g, '+').replace(/_/g, '/')),
    ) as Record<string, unknown>;
    const exp = typeof payload['exp'] === 'number' ? payload['exp'] : 0;
    return Math.max(0, exp - Date.now() / 1000);
  } catch {
    return 0;
  }
}

async function resolveToken(): Promise<string> {
  const token = useAuth.getState().accessToken;
  if (!token) throw new Error('Not authenticated — cannot connect to gateway');

  // Refresh proactively when the token expires within 120 seconds so that
  // the WebSocket always opens with a fresh credential.
  if (tokenSecondsRemaining(token) < 120) {
    const ok = await useAuth.getState().refresh();
    if (!ok) throw new Error('Not authenticated — token expired and refresh failed');
  }

  const fresh = useAuth.getState().accessToken;
  if (!fresh) throw new Error('Not authenticated — cannot connect to gateway');
  return fresh;
}

export async function createWireClient(worldId?: number): Promise<WireClient> {
  if (shouldUseMock()) {
    return new WireMockServer(freshHandshake);
  }
  let url = resolveGatewayUrl();
  if (worldId != null) {
    const sep = url.includes('?') ? '&' : '?';
    url = `${url}${sep}world_id=${worldId}`;
  }
  return new WebSocketWireClient({
    url,
    token: await resolveToken(),
  });
}

export async function bootstrapWireClient(worldId?: number): Promise<() => void> {
  const client = await createWireClient(worldId);
  useLiveWire.getState().setClient(client);

  const unsubscribe = client.onMessage((msg) => {
    // World-model path first — snapshot + event envelopes land in the
    // appliers, chat payloads are demuxed below.
    useWorldModel.getState().applyServerMessage(msg);
    routePingThrottle(msg);
    routeChat(msg);
    routeDms(msg);
    routeErrors(msg);
    routePresence(msg);
    routeNavigation(msg);
  });

  const selfPresence = createSelfPresenceDriver();

  // Reset the per-tick throttle buckets every time the world-model
  // advances to a new tick (D16 + Q-C3). Per-minute buckets prune
  // themselves on read.
  //
  // Use a closure-scoped `lastSeenTick` rather than zustand's
  // `(s, prev)` diff: events mutate `model.tick` in place (the Event
  // branch of `applyServerMessage` returns the same `s.model` ref),
  // which makes both `s.model` and `prev.model` point to the same
  // object inside the subscription callback. The reference comparison
  // never fires `resetTick` for event batches, so per-tick buckets
  // accumulate until the next snapshot — manifesting in J1 as the
  // second click-to-move tripping "rate limit: 1 move/tick" forever
  // after the first move. Capturing the primitive tick value side-
  // steps the mutation problem.
  let lastSeenTick = useWorldModel.getState().model.tick;
  const unsubThrottle = useWorldModel.subscribe((s) => {
    if (s.model.tick !== lastSeenTick) {
      lastSeenTick = s.model.tick;
      useThrottle.getState().resetTick(s.model.tick);
    }
  });

  phase0.info('bootstrap_begin', {
    transport: shouldUseMock() ? 'mock' : 'websocket',
    gateway_url: shouldUseMock() ? undefined : resolveGatewayUrl(),
  });
  debugLog('wire', 'info', `bootstrap transport=${shouldUseMock() ? 'mock' : 'websocket'}`);
  try {
    await client.connect(HANDSHAKE);
    phase0.info('bootstrap_connected');
    debugLog('wire', 'info', 'bootstrap connected');
  } catch (err) {
    phase0.error('wire_connect_failed', {
      reason: err instanceof Error ? err.message : String(err),
    });
    debugLog('wire', 'error', `connect_failed reason=${err instanceof Error ? err.message : String(err)}`);
  }
  return () => {
    unsubThrottle();
    selfPresence.destroy();
    unsubscribe();
    client.close();
    if (useLiveWire.getState().client === client) {
      useLiveWire.getState().setClient(null);
    }
  };
}

function routePresence(msg: ServerMessage): void {
  if (msg.type !== 'Event') return;
  if (msg.payload.envelope_kind !== 'PresenceUpdate') return;
  const { player, presence, since } = msg.payload.data;
  usePresence.getState().setOther(player, presence, since);
}

function routeChat(msg: ServerMessage): void {
  if (msg.type !== 'Event') return;
  const env = msg.payload;

  if (env.envelope_kind === 'WorldEvents') {
    for (const event of env.data.events) {
      if ('Spoke' in event) {
        useChat.getState().appendZoneMessage({
          kind: 'utterance',
          speaker: event.Spoke.speaker,
          text: event.Spoke.utterance,
          tick: env.data.tick,
        });
      } else if ('ZoneSystemMessage' in event) {
        useChat.getState().appendSystemMessage({
          text: systemMessageText(event.ZoneSystemMessage.kind),
          tick: env.data.tick,
        });
      }
    }
    return;
  }

  if (env.envelope_kind === 'WorldChat') {
    useChat.getState().appendWorldBroadcast({
      kind: 'broadcast',
      speaker: env.data.speaker,
      text: env.data.text,
      tick: env.data.sent_at,
    });
    return;
  }

  if (env.envelope_kind === 'ZoneBufferSnapshot') {
    useChat.getState().replaceZoneBuffer(
      env.data.buffer.map((m) => ({
        kind: 'utterance' as const,
        speaker: m.speaker,
        text: m.text,
        tick: m.sent_at,
      })),
    );
    return;
  }
}

function routeDms(msg: ServerMessage): void {
  if (msg.type !== 'Event') return;
  const env = msg.payload;

  if (env.envelope_kind === 'DmDelivery') {
    const payload = env.data;
    const state = useDms.getState();
    // Ensure the conversation exists so the list shows it even if we
    // never received a separate ConversationUpdate (e.g. offline-dev
    // WireMockServer echo).
    if (!state.conversations.has(payload.conversation_id)) {
      state.upsertConversation({
        id: payload.conversation_id,
        name: null,
        members: [payload.message.sender],
        last_message_at: payload.message.sent_at,
      });
    }
    state.appendMessage({
      id: payload.message.id,
      conversation_id: payload.conversation_id,
      sender: payload.message.sender,
      content: payload.message.content,
      sent_at: payload.message.sent_at,
      edited: payload.message.edited,
      deleted: payload.message.deleted,
    });
    return;
  }

  if (env.envelope_kind === 'ConversationUpdate') {
    const upd = env.data;
    const existing = useDms.getState().conversations.get(upd.conversation_id);
    if (upd.kind === 'Created') {
      useDms.getState().upsertConversation({
        id: upd.conversation_id,
        name: upd.detail ?? null,
        members: [upd.actor],
        last_message_at: 0n,
      });
      return;
    }
    if (!existing) return;
    let members = existing.members;
    let name = existing.name;
    if (upd.kind === 'MemberAdded' && !members.includes(upd.actor)) {
      members = [...members, upd.actor];
    } else if (upd.kind === 'MemberRemoved' || upd.kind === 'Left') {
      members = members.filter((m) => m !== upd.actor);
    } else if (upd.kind === 'Renamed') {
      name = upd.detail ?? null;
    }
    useDms.getState().upsertConversation({
      ...existing,
      members,
      name,
    });
  }
}

function routeErrors(msg: ServerMessage): void {
  if (msg.type !== 'Error') return;
  const { code, reason } = msg.payload;
  useToasts.getState().push(
    code === 'Forbidden' || code === 'RateLimited' ? 'warning' : 'error',
    `${code}: ${reason}`,
  );
}

function systemMessageText(
  kind: {
    PlayerEntered?: bigint;
    PlayerLeft?: bigint;
    SomaEntered?: number;
    SomaLeft?: number;
  },
): string {
  if ('PlayerEntered' in kind) return `player-${kind.PlayerEntered} entered the area`;
  if ('PlayerLeft' in kind) return `player-${kind.PlayerLeft} left the area`;
  if ('SomaEntered' in kind) return `soma-${kind.SomaEntered} entered the area`;
  if ('SomaLeft' in kind) return `soma-${kind.SomaLeft} left the area`;
  return '—';
}

// During idle periods the server skips empty event batches, so
// model.tick freezes and per-tick throttle buckets never reset.
// Pings carry server_tick — use it to keep the throttle current.
function routePingThrottle(msg: ServerMessage): void {
  if (msg.type !== 'Ping') return;
  const serverTick = msg.payload.server_tick;
  if (serverTick > useThrottle.getState().currentTick) {
    useThrottle.getState().resetTick(serverTick);
  }
}

/** Push a warning toast when the local player's navigation is cancelled
 *  due to `PathBlocked`. `Replaced` is silent (new destination clicked);
 *  `Disconnected` is handled by the connection error path. */
function routeNavigation(msg: ServerMessage): void {
  if (msg.type !== 'Event') return;
  if (msg.payload.envelope_kind !== 'WorldEvents') return;
  const selfId = useWorldModel.getState().selfPlayerId;
  if (selfId === null) return;
  for (const event of msg.payload.data.events) {
    if ('NavigationCancelled' in event) {
      const { player, reason } = event.NavigationCancelled;
      if (player === selfId && reason === 'PathBlocked') {
        useToasts.getState().push('warning', "Path blocked — can't reach that tile");
      }
    }
  }
}
